import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const GeneralLedger = ({ generalLedger }) => {
  if (!generalLedger || !generalLedger.accounts) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-slate-500">
          No general ledger data available. Select an LLC and date range to generate the report.
        </CardContent>
      </Card>
    );
  }

  const balanceDiff = Math.abs(generalLedger.grand_total_debits - generalLedger.grand_total_credits);
  const isBalanced = balanceDiff < 0.01;

  // Build account sections as arrays to avoid nested JSX maps
  const sections = [];
  const typeLabels = {
    'current_asset': 'Current Assets',
    'fixed_asset': 'Fixed Assets', 
    'other_asset': 'Other Assets',
    'current_liability': 'Current Liabilities',
    'long_term_liability': 'Long-term Liabilities',
    'equity': 'Equity',
    'income': 'Income',
    'expense': 'Expenses'
  };

  const typeOrder = ['current_asset', 'fixed_asset', 'other_asset', 'current_liability', 'long_term_liability', 'equity', 'income', 'expense'];
  
  typeOrder.forEach(type => {
    const accts = generalLedger.accounts.filter(a => a.account_type === type);
    if (accts.length > 0) {
      sections.push({ type, label: typeLabels[type], accounts: accts });
    }
  });

  return (
    <div className="space-y-6">
      <Card className="border-amber-200 bg-amber-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-serif text-amber-900">General Ledger Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-slate-600">Transactions</p>
              <p className="text-xl font-bold">{generalLedger.transaction_count}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Total Debits</p>
              <p className="text-xl font-bold text-green-700">{formatCurrency(generalLedger.grand_total_debits)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Total Credits</p>
              <p className="text-xl font-bold text-blue-700">{formatCurrency(generalLedger.grand_total_credits)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Difference</p>
              <p className={`text-xl font-bold ${isBalanced ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(balanceDiff)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {sections.map(section => (
        <GLSection key={section.type} section={section} />
      ))}
    </div>
  );
};

// Separate component to break up the nested structure
const GLSection = ({ section }) => (
  <div className="mb-6">
    <h3 className="text-lg font-semibold text-slate-700 border-b pb-2 mb-3">{section.label}</h3>
    {section.accounts.map((acc, i) => (
      <GLAccount key={i} account={acc} />
    ))}
  </div>
);

const GLAccount = ({ account }) => (
  <div className="border rounded-lg overflow-hidden mb-4">
    <div className="bg-slate-100 p-3 flex justify-between items-center">
      <span className="font-semibold">{account.account_name}</span>
      <span className="text-sm">
        Dr: {formatCurrency(account.total_debits)} | Cr: {formatCurrency(account.total_credits)}
      </span>
    </div>
    <table className="w-full">
      <thead className="bg-slate-50 text-xs">
        <tr>
          <th className="p-2 text-left w-24">Date</th>
          <th className="p-2 text-left">Description</th>
          <th className="p-2 text-left">Vendor/Payee</th>
          <th className="p-2 text-left w-20">Ref</th>
          <th className="p-2 text-right w-28">Debit</th>
          <th className="p-2 text-right w-28">Credit</th>
        </tr>
      </thead>
      <tbody>
        <GLTransactions transactions={account.transactions} />
      </tbody>
    </table>
  </div>
);

const GLTransactions = ({ transactions }) => (
  <>
    {transactions.map((txn, i) => (
      <tr key={i} className="border-t hover:bg-slate-50">
        <td className="p-2 text-sm">{formatDate(txn.date)}</td>
        <td className="p-2 text-sm">{txn.description || '-'}</td>
        <td className="p-2 text-sm text-slate-600">{txn.vendor_name || '-'}</td>
        <td className="p-2 text-sm text-slate-500">{txn.reference || '-'}</td>
        <td className="p-2 text-sm text-right text-green-700">{txn.debit ? formatCurrency(txn.debit) : ''}</td>
        <td className="p-2 text-sm text-right text-blue-700">{txn.credit ? formatCurrency(txn.credit) : ''}</td>
      </tr>
    ))}
  </>
);

export default GeneralLedger;
